import React, { Fragment } from 'react';
import gql from 'graphql-tag';
import GqlAutocomplete from './GqlAutocomplete';
import { joinDefined } from '../../utils/strings';
import { UserIcon } from '../IconIndex';
import { Query } from '../../reactApolloCompat';
import Spinner from '../loading/Spinner';
import { getClient } from '../../utils/apollo';
import { getUser } from '../../utils/sessions';
import AutoComplete from './AutoComplete';

const StaffAutoComplete = (
    {
        category = 'cm-users',
        className,
        name,
        defaultMe = true,
        clearOnSelect = true,
        preloadAll,
        maxHeight,
        limit = 7,
        ...props
    }
) => {
    if (preloadAll) {
        let query = null;
        if (typeof category === 'string') {
            query = gql`
                query ReadAllMembers($category: String) {
                    readMembers(groupCode: $category, limit: 500) {
                        edges {
                            node {
                                ID
                                Email
                                Surname
                                FirstName
                            }
                        }
                    }
                }
            `;
        } else if (Array.isArray(category)) {
            query = gql`
                query ReadAllMembers {
                    ${category.map((cat, index) => {
                        return `members${index}: readMembers(groupCode: "${cat}", limit: 100) {
                            edges {
                                node {
                                    ID
                                    Email
                                    Surname
                                    FirstName
                                }
                            }
                        }`;
                    })}
                }
            `;
        }
        return (
            <Query query={query} variables={{ category }} client={getClient()}>
                {({ data, loading, error }) => {
                    if (loading)
                        return (
                            <Fragment>
                                <Spinner />
                                Loading Staff Members
                            </Fragment>
                        );

                    if (error) return <Fragment>Failed to load staff members</Fragment>;
                    const members = [];
                    let memberIDs = [];
                    for (let memberGroup in data) {
                        const group = data[memberGroup].edges;
                        for (let i = 0; i < group.length; i++) {
                            let member = group[i].node;
                            member.key = member.ID;
                            member.label = joinDefined([member.FirstName, member.Surname], ' ');
                            if (memberIDs.indexOf(member.ID) < 0) {
                                members.push(member);
                                memberIDs.push(member.ID);
                            }
                        }
                    }
                    members.sort((member1, member2) => {
                        return member1.label.localeCompare(member2.label);
                    });
                    memberIDs = members.map(member => {
                        return member.ID;
                    });
                    const { value } = props;
                    let matchIdx = memberIDs.indexOf((value && value.ID) || value);
                    const me = getUser();
                    if (matchIdx < 0 && defaultMe) {
                        matchIdx = memberIDs.indexOf('' + me.ID);
                    }
                    const valueText =
                        matchIdx > -1
                            ? members[matchIdx].label
                            : value === me.ID
                                ? joinDefined([me.FirstName, me.Surname], ' ')
                                : '';
                    const validMembers = members.filter(obj => !obj.Deactivated);
                    return (
                        <AutoComplete
                            label="Staff member..."
                            options={validMembers}
                            limit={limit}
                            value={valueText}
                            onChange={result => props.onSelect && props.onSelect(null, result)}
                            maxHeight={maxHeight}
                            clearOnSelect={clearOnSelect}
                        />
                    );
                }}
            </Query>
        );
    }
    return (
        <GqlAutocomplete
            findById
            className={className}
            readAllQuery={readAllStaff}
            readOneQuery={readOneStaff}
            placeholder="Search for staff..."
            labelFieldFunc={e => joinDefined([e.FirstName, e.Surname], ' ')}
            name={name}
            clearOnSelect={clearOnSelect}
            limit={limit}
            minChars={2}
            extraVariables={{ codes: category || 'cm-users' }}
            icon={UserIcon}
            {...props}
        />
    );
};

const memberFragment = gql`
    fragment MemberFragment on Member {
        ID
        Email
        Surname
        FirstName
    }
`;

const readOneStaff = gql`
    ${memberFragment}
    query ReadMember($id: ID!) {
        readOneMember(ID: $id) {
            ...MemberFragment
        }
    }
`;

const readAllStaff = gql`
    ${memberFragment}

    query ReadMembers($contains: String, $limit: Int, $codes: String) {
        readMembers(contains: $contains, limit: $limit, offset: 0, activeOnly: true, groupCode: $codes) {
            edges {
                node {
                    ...MemberFragment
                }
            }
            pageInfo {
                totalCount
            }
        }
    }
`;

export default StaffAutoComplete;
